import { createRef } from 'react';
import { createPortal } from 'react-dom';

import '../../../JxControls/JxTheme.css';

import { JxButton, JxFormResizableBase, JxLineEdit } from '@jx';
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar';

import { TITLE_BAR_ACTIONS } from './UmlPkgSaveDlgTitleBarActions';
import { createSchema, createEmpty } from './UmlPkgSaveDlgSchema';
import umlPkgLibSaver from '../Core/UmlPkgLibSaver';
import umlPkgLibPreviewBuilder from '../Core/UmlPkgLibPreviewBuilder';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../services/wss/events/EngineNames';

import './UmlPkgSaveDlg.css';

/**
 * "Copy Package" save dialog
 */
export default class UmlPkgSaveDlg extends JxFormResizableBase {
    previewRef = createRef();

    constructor(props) {
        super(props);
        this.name = 'UmlPkgSaveDlg';
        this.schema = createSchema();
        this.state = { ...this.state, open: false, packageShape: null, inputs: createEmpty(null), isSaving: false, previewDataUrl: null };
    }

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribe = componentNotifier.subscribe(EngineNames.UmlPkgSaveDlg, this.handleNotify);
    }

    componentWillUnmount() {
        this._unsubscribe?.();
        super.componentWillUnmount();
    }

    /**
     * Self-contained open trigger
     * @param {{ target: string, data: object }} e
     */
    handleNotify = (e) => {
        if (e.target !== 'Open') return;
        this.setState({ open: true, packageShape: e.data, inputs: createEmpty(e.data), previewDataUrl: null }, () => {
            this.bringToTop();
            this.capturePreview();
        });
    };

    _handleKeyDown = (e) => {
        if (!this.isActive()) return;
        if (e.key !== 'Escape') return;
        if (!this.state.open) return;
        this.handleClose(-1);
    };

    /**
     * Builds a package+satellites preview thumbnail via UmlPkgLibPreviewBuilder
     * (shared with the library's own preview capture — was duplicated inline here before).
     */
    capturePreview = async () => {
        try {
            const dataUrl = await umlPkgLibPreviewBuilder.build(this.state.packageShape, this.previewRef.current);
            if (dataUrl) this.setState({ previewDataUrl: dataUrl });
        } catch (error) {
            this.logError('capturePreview', error.message);
        }
    };

    getDefaultGeo() {
        return { top: 180, left: 138, width: 380, height: 480 };
    }

    handleClose = (action) => {
        try {
            this.setState({ open: false, packageShape: null });
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    handleSave = async () => {
        if (!this.state.inputs.name?.trim()) return;
        this.setState({ isSaving: true });
        try {
            await umlPkgLibSaver.saveTo(
                this.state.packageShape,
                umlCanvasEngine.shapes,
                umlConnectorEngine.connectors,
                this.state.inputs,
                this.state.previewDataUrl,
            );
            this.handleClose(0);
        } catch (error) {
            this.logError('handleSave', error.message);
        } finally {
            this.setState({ isSaving: false });
        }
    };

    render() {
        if (!this.state.open) return null;

        const panel = (
            <div {...this.panelProps} style={this.panelStyle} className={`jx-panel upsd-panel ${this.panelActiveClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Save Package to Library"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={TITLE_BAR_ACTIONS(() => this.handleClose(-1))}
                />

                <div className="upsd-body">
                    <JxLineEdit
                        id="upsd_name"
                        value={this.state.inputs.name}
                        onChange={(v) => this.handleChange('name', v)}
                        placeholder="Package name"
                        maxLength={256}
                        error={this.state.errors?.name}
                    />

                    <div id="upsd_preview" className="upsd-preview">
                        {this.state.previewDataUrl ? (
                            <img className="upsd-preview-image" src={this.state.previewDataUrl} alt="Package preview" />
                        ) : (
                            <div className="upsd-preview-capture" ref={this.previewRef}>
                                <span className="upsd-preview-placeholder">Generating preview…</span>
                            </div>
                        )}
                    </div>

                    <textarea
                        className="upsd-remarks"
                        value={this.state.inputs.remarks}
                        onChange={(e) => this.handleChange('remarks', e.target.value)}
                        placeholder="Remarks (optional)"
                        maxLength={2000}
                    />
                </div>

                <div className="upsd-footer">
                    <JxButton label="Cancel" onClick={() => this.handleClose(-1)} />
                    <JxButton className="jx-button-accent" label="Save" onClick={this.handleSave} disabled={this.state.isSaving} />
                </div>

                {this.renderResizeHandles()}
            </div>
        );

        return createPortal(panel, document.body);
    }
}
